import { useCallback } from 'react';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import {
  createSlot,
  updateSlot,
  deleteSlot,
  shiftSlotsDate,
  batchPatchSlotLocationVisibility,
  type BatchSlotLocationVisibilityItem,
} from '../../api/schedule';
import { SlotInfo } from '../../types/schedule.types';

/**
 * Slot 更新 Hook 配置选项
 */
export interface useSlotUpdatesOptions {
  onSuccess?: (action: 'create' | 'update' | 'delete' | 'shift', data: any) => void;
  onError?: (error: Error, action: string, data: any) => void;
}

/**
 * Slot 更新 Hook（各方法的第一个参数均为拍摄计划 ID：`ScheduleInfo.scheduleID`）
 * 提供统一的 Slot 数据更新方法，包括：
 * - 单条/批量更新 Slot
 * - 单条/批量删除 Slot
 * - 单条创建 Slot
 * - 批量移动 Slot 日期
 * 
 * 更新流程：
 * 1. 乐观更新缓存
 * 2. 通知本标签页订阅者（UI 立即更新）
 * 3. 调用 API
 * 4. 成功：通过 BroadcastChannel 通知其他标签页
 * 5. 失败：回滚缓存，通知订阅者
 */
export function useSlotUpdates(options?: useSlotUpdatesOptions) {
  const { getSlotCache, setSlotCache } = useDataCache();

  /**
   * 创建单个 Slot
   * @returns 新创建的 slotId
   */
  const createSlotData = useCallback(
    async (scheduleId: string, data: Partial<SlotInfo>): Promise<string> => {
      try {
        // 调用 API 创建
        const response = await createSlot(data);
        const newSlotId = response.data?.slotID;

        if (!newSlotId) {
          throw new Error('创建 Slot 失败：未返回 slotID');
        }

        // 更新缓存（添加新创建的 slot）
        const cache = getSlotCache(scheduleId);
        if (cache) {
          const newSlot = {
            ...data,
            slotID: newSlotId,
          } as SlotInfo;

          setSlotCache(scheduleId, {
            slots: [...cache.slots, newSlot],
          });
        }

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('create', { slotId: newSlotId, data });
        }

        return newSlotId;
      } catch (error) {
        console.error('[useSlotUpdates] 创建 Slot 失败:', error);

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'create', { data });
        }

        throw error;
      }
    },
    [getSlotCache, setSlotCache, options]
  );

  /**
   * 更新单个 Slot
   */
  const updateSlotData = useCallback(
    async (scheduleId: string, slotId: string, updates: Partial<SlotInfo>) => {
      let oldCache: any = null;
      let oldSlots: any[] = [];

      try {
        // 乐观更新缓存
        const cache = getSlotCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldSlots = [...cache.slots];

          const updatedSlots = cache.slots.map((slot) =>
            slot.slotID === slotId ? { ...slot, ...updates } : slot
          );

          setSlotCache(scheduleId, {
            slots: updatedSlots,
          });
        }

        // 调用 API
        await updateSlot(slotId, updates);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('update', { slotId, updates });
        }
      } catch (error) {
        console.error('[useSlotUpdates] 更新 Slot 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setSlotCache(scheduleId, {
            slots: oldSlots,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'update', { slotId, updates });
        }

        throw error;
      }
    },
    [getSlotCache, setSlotCache, options]
  );

  /**
   * 批量更新 Slots
   */
  const updateSlotsData = useCallback(
    async (
      scheduleId: string,
      updates: Array<{ slotId: string; data: Partial<SlotInfo> }>
    ) => {
      let oldCache: any = null;
      let oldSlots: any[] = [];

      try {
        // 创建更新映射
        const updateMap = new Map(updates.map((u) => [u.slotId, u.data]));

        // 乐观更新缓存
        const cache = getSlotCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldSlots = [...cache.slots];

          const updatedSlots = cache.slots.map((slot) => {
            const update = updateMap.get(slot.slotID);
            return update ? { ...slot, ...update } : slot;
          });

          setSlotCache(scheduleId, {
            slots: updatedSlots,
          });
        }

        // 批量调用 API
        await Promise.all(
          updates.map((update) => updateSlot(update.slotId, update.data))
        );

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('update', { updates });
        }
      } catch (error) {
        console.error('[useSlotUpdates] 批量更新 Slots 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setSlotCache(scheduleId, {
            slots: oldSlots,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'update', { updates });
        }

        throw error;
      }
    },
    [getSlotCache, setSlotCache, options]
  );

  /**
   * 删除单个 Slot
   */
  const deleteSlotData = useCallback(
    async (scheduleId: string, slotId: string) => {
      let oldCache: any = null;
      let oldSlots: any[] = [];

      try {
        // 乐观删除缓存
        const cache = getSlotCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldSlots = [...cache.slots];

          const updatedSlots = cache.slots.filter(
            (slot) => slot.slotID !== slotId
          );

          setSlotCache(scheduleId, {
            slots: updatedSlots,
          });
        }

        // 调用 API
        await deleteSlot(slotId);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { slotId });
        }
      } catch (error) {
        console.error('[useSlotUpdates] 删除 Slot 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setSlotCache(scheduleId, {
            slots: oldSlots,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { slotId });
        }

        throw error;
      }
    },
    [getSlotCache, setSlotCache, options]
  );

  /**
   * 批量删除 Slots
   */
  const deleteSlotsData = useCallback(
    async (scheduleId: string, slotIds: string[]) => {
      let oldCache: any = null;
      let oldSlots: any[] = [];

      try {
        const slotIdSet = new Set(slotIds);

        // 乐观删除缓存
        const cache = getSlotCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldSlots = [...cache.slots];

          const updatedSlots = cache.slots.filter(
            (slot) => !slotIdSet.has(slot.slotID)
          );

          setSlotCache(scheduleId, {
            slots: updatedSlots,
          });
        }

        // 批量调用 API
        await Promise.all(slotIds.map((id) => deleteSlot(id)));

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { slotIds });
        }
      } catch (error) {
        console.error('[useSlotUpdates] 批量删除 Slots 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setSlotCache(scheduleId, {
            slots: oldSlots,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { slotIds });
        }

        throw error;
      }
    },
    [getSlotCache, setSlotCache, options]
  );

  /**
   * 批量更新地点头显示（乐观更新缓存 + 单次 API，失败回滚）
   */
  const batchPatchSlotLocationVisibilityData = useCallback(
    async (scheduleId: string, items: BatchSlotLocationVisibilityItem[]) => {
      if (!items.length) return;

      let oldCache: any = null;
      let oldSlots: SlotInfo[] = [];

      try {
        const itemMap = new Map(items.map((i) => [i.slotID, i]));

        const cache = getSlotCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldSlots = [...cache.slots];

          const updatedSlots = cache.slots.map((slot) => {
            const u = itemMap.get(slot.slotID);
            if (!u) return slot;
            return {
              ...slot,
              slotLocationVisibility: u.slotLocationVisibility,
              ...(u.slotShift !== undefined ? { slotShift: u.slotShift } : {}),
            };
          });

          setSlotCache(scheduleId, {
            slots: updatedSlots,
          });
        }

        const res = await batchPatchSlotLocationVisibility({ items });
        const serverSolts = res.data ?? [];
        if (serverSolts.length > 0) {
          const after = getSlotCache(scheduleId);
          if (after?.slots) {
            const byId = new Map(serverSolts.map((t) => [t.slotID, t]));
            setSlotCache(scheduleId, {
              slots: after.slots.map((t) => {
                const u = byId.get(t.slotID);
                return u ? { ...t, ...u } : t;
              }),
            });
          }
        }

        if (options?.onSuccess) {
          options.onSuccess('update', { items, serverSolts });
        }
      } catch (error) {
        console.error('[useSlotUpdates] 批量更新地点头显示失败:', error);

        if (oldCache) {
          setSlotCache(scheduleId, {
            slots: oldSlots,
          });
        }

        if (options?.onError) {
          options.onError(error as Error, 'update', { items });
        }

        throw error;
      }
    },
    [getSlotCache, setSlotCache, options],
  );

  /** 批量移动范围内 Slot 的拍摄日（按天数平移），乐观更新缓存后调 API */
  const shiftSlotsDateData = useCallback(
    async (scheduleId: string, slotIds: string[], daysOffset: number) => {
      let oldCache: any = null;
      let oldSlots: any[] = [];

      try {
        const slotIdSet = new Set(slotIds);

        // 乐观更新缓存（移动日期）
        const cache = getSlotCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldSlots = [...cache.slots];

          const updatedSlots = cache.slots.map((slot) => {
            if (slotIdSet.has(slot.slotID)) {
              // 计算新日期
              const oldDate = slot.slotDate ? new Date(slot.slotDate) : null;
              const oldEndDate = slot.slotEndDay ? new Date(slot.slotEndDay) : null;

              if (oldDate) {
                const newDate = new Date(oldDate);
                newDate.setDate(newDate.getDate() + daysOffset);
                const newDateStr = newDate.toISOString().split('T')[0];

                const updates: any = {
                  slotDate: newDateStr,
                };

                if (oldEndDate) {
                  const newEndDate = new Date(oldEndDate);
                  newEndDate.setDate(newEndDate.getDate() + daysOffset);
                  updates.slotEndDay = newEndDate.toISOString().split('T')[0];
                }

                return { ...slot, ...updates };
              }
            }
            return slot;
          });

          setSlotCache(scheduleId, {
            slots: updatedSlots,
          });
        }

        // 调用 API
        await shiftSlotsDate(slotIds, daysOffset);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('shift', { slotIds, daysOffset });
        }
      } catch (error) {
        console.error('[useSlotUpdates] 批量移动 Slot 日期失败:', error);

        // 回滚缓存
        if (oldCache) {
          setSlotCache(scheduleId, {
            slots: oldSlots,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'shift', { slotIds, daysOffset });
        }

        throw error;
      }
    },
    [getSlotCache, setSlotCache, options]
  );

  return {
    createSlotData,
    updateSlotData,
    updateSlotsData,
    deleteSlotData,
    deleteSlotsData,
    batchPatchSlotLocationVisibilityData,
    shiftSlotsDateData,
  };
}
